import { api } from "@convex/_generated/api";
import { useRouterState } from "@tanstack/react-router";
/**
 * useAgentContext Hook (V4: Context Awareness)
 *
 * Automatically tracks user navigation and updates the AI context.
 * This enables the AI agent to understand what the user is currently viewing
 * and provide more relevant, context-aware responses.
 *
 * Usage:
 * - Add to the authenticated layout to track all navigation
 * - The hook automatically extracts page type and entity from routes
 * - Context is updated in Convex on every navigation
 */
import { useAction, useMutation, useQuery } from "convex/react";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  useActiveOrganizationConvexId,
  useCurrentUser,
} from "~/integrations/vortex-auth/hooks";
import { toOptionalConvexTableId } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";

import type {
  ParsedContext,
  UseAgentContextOptions,
  UseAgentContextReturn,
} from "./agent-context-types";
import { parseRoute } from "./parse-route";

// Re-export types for consumers
export type {
  ContextSuggestion,
  EntityType,
  PageType,
  ParsedContext,
  UseAgentContextOptions,
  UseAgentContextReturn,
} from "./agent-context-types";

// Avoids TS2589 (type instantiation is excessively deep) from api.d.ts chain
// Types are defined in the TYPES section below; these refs use them after hoisting.
const getCachedSuggestionsPublicRef = api.ai.context.getCachedSuggestionsPublic;

const refreshSuggestionsRef = api.ai.context.refreshSuggestions;

const updateContextRef = api.ai.context.updateContext;

import { isValidConvexId } from "@plasma/utils/convex-id";

// =============================================================================
// HOOK IMPLEMENTATION
// =============================================================================

export function useAgentContext(
  options: UseAgentContextOptions = {}
): UseAgentContextReturn {
  const { enabled = true, debounceMs = 300 } = options;

  // Router state
  const routerState = useRouterState();
  const { pathname } = routerState.location;

  // Auth state (vortex-auth)
  const { user } = useCurrentUser();
  const userId = user?.id;

  // Active organization's Convex document id (replaces the legacy
  // getOrganizationByOrgId round-trip).
  const { organizationId } = useActiveOrganizationConvexId();
  const organizationInfo = organizationId ? { id: organizationId } : null;

  // Context mutation
  const updateContextMutation = useMutation(updateContextRef);

  // Track current context
  const contextReference = useRef<ParsedContext | null>(null);
  const debounceTimerReference = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const lastPathnameReference = useRef<string>("");

  // Manual entity override (for modals that don't update URL)
  const [manualEntity, setManualEntity] = useState<
    ParsedContext["entity"] | undefined
  >();

  // Parse current route
  const routeContext = parseRoute(pathname);

  // Merge route context with manual entity override
  const currentContext: ParsedContext = {
    ...routeContext,
    entity: manualEntity ?? routeContext.entity,
  };
  contextReference.current = currentContext;

  // Update context when route changes
  useEffect(() => {
    if (!(enabled && userId && organizationInfo?.id)) {
      return undefined;
    }

    // Skip if pathname hasn't changed
    if (pathname === lastPathnameReference.current) {
      return undefined;
    }
    lastPathnameReference.current = pathname;

    // Clear existing debounce timer
    if (debounceTimerReference.current) {
      clearTimeout(debounceTimerReference.current);
    }

    // Debounce the context update
    debounceTimerReference.current = setTimeout(() => {
      const context = parseRoute(pathname);

      // Validate IDs before sending to Convex to prevent ArgumentValidationErrors
      const hasInvalidEntityId = Boolean(
        context.entity?.id && !isValidConvexId(context.entity.id)
      );
      const hasInvalidLocationId = Boolean(
        context.locationId && !isValidConvexId(context.locationId)
      );

      if (hasInvalidEntityId || hasInvalidLocationId) {
        // Skip context update for invalid IDs (e.g., user typed garbage in URL)
        return;
      }

      updateContextMutation({
        userId,
        organizationId: organizationInfo.id,
        currentPage: context.currentPage,
        pageType: context.pageType,
        currentEntity: context.entity,
        locationId: toOptionalConvexTableId("locations", context.locationId),
        metadata: context.metadata,
      }).catch((error) => {
        // Log but don't fail - context tracking is optional enhancement
        logger.warn("agent_context_update_failed", { error: String(error) });
      });
    }, debounceMs);

    // Cleanup
    return () => {
      if (debounceTimerReference.current) {
        clearTimeout(debounceTimerReference.current);
      }
    };
  }, [
    enabled,
    pathname,
    userId,
    organizationInfo?.id,
    updateContextMutation,
    debounceMs,
  ]);

  // Manual metadata update
  const updateMetadata = useCallback(
    async (metadata: ParsedContext["metadata"]) => {
      if (!(userId && organizationInfo?.id && contextReference.current)) {
        return;
      }

      const context = contextReference.current;

      // Validate IDs before sending
      const hasInvalidEntityId = Boolean(
        context.entity?.id && !isValidConvexId(context.entity.id)
      );
      const hasInvalidLocationId = Boolean(
        context.locationId && !isValidConvexId(context.locationId)
      );

      if (hasInvalidEntityId || hasInvalidLocationId) {
        return;
      }

      try {
        await updateContextMutation({
          userId,
          organizationId: organizationInfo.id,
          currentPage: context.currentPage,
          pageType: context.pageType,
          currentEntity: context.entity,
          locationId: toOptionalConvexTableId("locations", context.locationId),
          metadata: {
            ...context.metadata,
            ...metadata,
          },
        });
      } catch (error) {
        logger.warn("agent_context_metadata_update_failed", {
          error: String(error),
        });
      }
    },
    [userId, organizationInfo?.id, updateContextMutation]
  );

  // Manual entity setter (for modals/dialogs)
  const setEntity = useCallback(
    async (entity: ParsedContext["entity"] | undefined) => {
      setManualEntity(entity);

      // Also update Convex context immediately
      if (!(userId && organizationInfo?.id && contextReference.current)) {
        return;
      }

      // Validate entity ID if provided
      if (entity?.id && !isValidConvexId(entity.id)) {
        return;
      }

      const context = contextReference.current;

      // Validate locationId
      const hasInvalidLocationId = Boolean(
        context.locationId && !isValidConvexId(context.locationId)
      );
      if (hasInvalidLocationId) {
        return;
      }

      try {
        await updateContextMutation({
          userId,
          organizationId: organizationInfo.id,
          currentPage: context.currentPage,
          pageType: context.pageType,
          currentEntity: entity,
          locationId: toOptionalConvexTableId("locations", context.locationId),
          metadata: context.metadata,
        });
      } catch (error) {
        logger.warn("agent_context_entity_update_failed", {
          error: String(error),
        });
      }
    },
    [userId, organizationInfo?.id, updateContextMutation]
  );

  return {
    context: currentContext,
    isTracking: enabled && Boolean(userId && organizationInfo?.id),
    updateMetadata,
    setEntity,
  };
}

/**
 * Hook to get LLM-generated suggestions based on current context.
 *
 * V4.2: Optimistic suggestion loading with instant cache reads.
 * Uses a reactive query to get cached suggestions instantly (no loading state),
 * then triggers a background refresh if cache is stale or missing.
 *
 * This ensures the UI never shows a loading spinner for suggestions -
 * users always see either cached or fallback suggestions immediately.
 *
 * @param options.enabled - Whether to fetch suggestions (default: true)
 * @returns Object with suggestions array, loading state, and refresh function
 */
export function useLLMSuggestions(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  const { user } = useCurrentUser();
  const userId = user?.id;
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const { organizationId } = useActiveOrganizationConvexId();
  const organizationInfo = organizationId ? { id: organizationId } : null;

  // V4.2: Use reactive query for instant cached suggestions
  // This returns immediately with cached data or fallback
  const cachedResult = useQuery(
    getCachedSuggestionsPublicRef,
    enabled && userId && organizationInfo?.id
      ? { userId, organizationId: organizationInfo.id }
      : "skip"
  );

  const refreshSuggestionsAction = useAction(refreshSuggestionsRef);
  const lastRefreshReference = useRef<string>("");

  // Background refresh when cache is stale (fire and forget)
  const refreshInBackground = useCallback(async () => {
    if (!(enabled && userId && organizationInfo?.id)) {
      return;
    }

    // Debounce: don't refresh if we already refreshed for this context
    const key = `${cachedResult?.pageType}-${cachedResult?.entityType}`;
    if (key === lastRefreshReference.current) {
      return;
    }
    lastRefreshReference.current = key;

    setIsRefreshing(true);
    try {
      await refreshSuggestionsAction({
        userId,
        organizationId: organizationInfo.id,
      });
    } catch (error_) {
      logger.warn("llm_suggestions_refresh_failed", { error: String(error_) });
      setError(error_ instanceof Error ? error_ : new Error(String(error_)));
    } finally {
      setIsRefreshing(false);
    }
  }, [
    enabled,
    userId,
    organizationInfo?.id,
    cachedResult?.pageType,
    cachedResult?.entityType,
    refreshSuggestionsAction,
  ]);

  // Trigger background refresh if cache miss (but don't block UI)
  useEffect(() => {
    if (cachedResult && !cachedResult.isCached) {
      // Cache miss - refresh in background
      void refreshInBackground();
    }
  }, [cachedResult, refreshInBackground]);

  return {
    // Always return suggestions (cached or fallback) - never empty while loading
    suggestions: cachedResult?.suggestions ?? [],
    // Only show loading if we have no suggestions at all
    isLoading: !cachedResult,
    // Show refreshing state for UI hint (optional)
    isRefreshing,
    error,
    refresh: refreshInBackground,
  };
}
